import { create } from "zustand";

/**
 * Shared background-job tracking (VP-113, lista jobów: VP-206).
 *
 * Long-running generations (graphics now, video later) run server-side as
 * detached jobs and return a `jobId`. The bug this fixes: the active jobId used
 * to live in the generating component's local `useState`, so leaving the view
 * unmounted the component and the frontend lost the job — no live progress, no
 * 1:1 return. Holding active jobIds here, OUTSIDE component state, lets the
 * view resume the same live polling when the user navigates back.
 *
 * VP-206: per kind trzymamy LISTĘ jobów (max współbieżność pilnowana przez
 * backend + blokadę przycisku), nie pojedynczy slot — user może zlecić kolejny
 * prompt, gdy poprzednie generowania jeszcze trwają.
 *
 * `kind` keys the surface so this generalizes to future generators without
 * touching them; only 'graphic' is wired today.
 */
export type BackgroundJobKind = "graphic";

type BackgroundJobsState = {
  /** jobIds currently generating per surface, w kolejności zgłoszenia. */
  activeJobIdsByKind: Partial<Record<BackgroundJobKind, string[]>>;
  addActiveJob: (kind: BackgroundJobKind, jobId: string) => void;
  removeActiveJob: (kind: BackgroundJobKind, jobId: string) => void;
};

export const useBackgroundJobsStore = create<BackgroundJobsState>((set) => ({
  activeJobIdsByKind: {},
  addActiveJob: (kind, jobId) =>
    set((state) => {
      const current = state.activeJobIdsByKind[kind] ?? [];
      if (current.includes(jobId)) return state;
      return { activeJobIdsByKind: { ...state.activeJobIdsByKind, [kind]: [...current, jobId] } };
    }),
  removeActiveJob: (kind, jobId) =>
    set((state) => {
      const current = state.activeJobIdsByKind[kind] ?? [];
      if (!current.includes(jobId)) return state;
      return {
        activeJobIdsByKind: {
          ...state.activeJobIdsByKind,
          [kind]: current.filter((id) => id !== jobId),
        },
      };
    }),
}));
